import { isToday, parseISO } from 'date-fns';

/**
 * Booking times in the service area's zone, whatever zone the browser is in.
 *
 * The server builds a slot's `date` as a wall time in the franchise's zone
 * (no offset), and sends the same start as an instant in `startsAt` with the
 * zone in `timeZone`. Drawing `date` with parseISO shows that wall time
 * as-is in any browser; booking must use `startsAt`, or a browser in another
 * zone (a traveller, an agent's UTC browser) books a different instant. An
 * older server sends neither field: then `date` is the browser's own wall
 * time, as before.
 */

export interface SlotTime {
  date: string;
  startsAt?: string | null;
  timeZone?: string | null;
}

/** The instant a slot starts: `startsAt` when the server sent it. */
export function slotInstant(slot: SlotTime): Date {
  if (slot.startsAt) {
    const instant = new Date(slot.startsAt);
    if (!Number.isNaN(instant.getTime())) return instant;
  }
  return parseISO(slot.date);
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

/** The wall clock of `instant` in `zone`, e.g. "10/28/2026, 07:00". */
function wallClock(instant: Date, zone?: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: zone || undefined,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(instant);
}

/** True when the browser's clock reads differently from the slot's zone. */
export function zoneDiffers(slot?: SlotTime | null): boolean {
  if (!slot?.timeZone) return false;
  const browser = browserTimeZone();
  if (browser && browser === slot.timeZone) return false;
  try {
    const instant = slotInstant(slot);
    return wallClock(instant, slot.timeZone) !== wallClock(instant);
  } catch {
    return false;
  }
}

/** "PT", "CT"... (or "PDT" where the generic name is not available). */
export function zoneAbbreviation(zone: string, instant: Date): string {
  const part = (style: 'shortGeneric' | 'short'): string => {
    try {
      const options = {
        timeZone: zone,
        timeZoneName: style,
      } as Intl.DateTimeFormatOptions;
      return new Intl.DateTimeFormat('en-US', options)
        .formatToParts(instant)
        .find((p) => p.type === 'timeZoneName')?.value || '';
    } catch {
      return '';
    }
  };
  const generic = part('shortGeneric');
  if (generic && !/^GMT/.test(generic)) return generic;
  return part('short') || zone;
}

/**
 * " PT" after a time when the browser is in another zone, so "7:00 am PT"
 * says which 7:00; "" when the clocks agree (every customer at home).
 */
export function zoneSuffix(slot?: SlotTime | null): string {
  if (!slot?.timeZone || !zoneDiffers(slot)) return '';
  return ` ${zoneAbbreviation(slot.timeZone, slotInstant(slot))}`;
}

/** Whether the slot is today in its own zone (the same-day fee). */
export function isSlotToday(slot: SlotTime, now: Date = new Date()): boolean {
  if (!slot.timeZone) return isToday(parseISO(slot.date));
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: slot.timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
    const get = (type: string) => parts.find((p) => p.type === type)?.value;
    const today = `${get('year')}-${get('month')}-${get('day')}`;
    return slot.date.slice(0, 10) === today;
  } catch {
    return isToday(parseISO(slot.date));
  }
}
